// The currencies a MIS document can be written in — the browser's half of
// api/utils/currencies.js (which also holds the Arabic number-words a tax invoice prints).
// The codes, the decimals and the names must stay the same in both; the server is
// authoritative (it rounds the totals), this only draws them and lets a form show the
// figure the server will store.
//
// A document carries ONE currency, chosen per document with its branch's default as the
// starting point. A currency decides how many decimals an amount has: a dirham two, a
// rial none.

export const CURRENCIES = {
  AED: { code: 'AED', decimals: 2, name: { en: 'UAE dirham', ar: 'درهم إماراتي', fa: 'درهم امارات' } },
  SAR: { code: 'SAR', decimals: 2, name: { en: 'Saudi riyal', ar: 'ريال سعودي', fa: 'ریال عربستان' } },
  IRR: { code: 'IRR', decimals: 0, name: { en: 'Iranian rial', ar: 'ريال إيراني', fa: 'ریال ایران' } },
  USD: { code: 'USD', decimals: 2, name: { en: 'US dollar', ar: 'دولار أمريكي', fa: 'دلار آمریکا' } },
  EUR: { code: 'EUR', decimals: 2, name: { en: 'Euro', ar: 'يورو', fa: 'یورو' } },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);
export const DEFAULT_CURRENCY = 'AED';     // what every document and branch had before currencies were selectable

export const normalizeCurrency = (code, fallback = DEFAULT_CURRENCY) => {
  const c = String(code || '').trim().toUpperCase();
  return CURRENCIES[c] ? c : fallback;
};

export const decimalsOf = (code) => CURRENCIES[normalizeCurrency(code)].decimals;

/** Round an amount to its currency's decimals (a rial to a whole number, a dirham to fils). */
export function roundMoney(n, code = DEFAULT_CURRENCY) {
  const f = 10 ** decimalsOf(code);
  return Math.round(((Number(n) || 0) + Number.EPSILON) * f) / f;
}

/** "3,084.48" — or "1,250,000" for a rial. Latin digits, as every document prints them. */
export function formatMoney(n, code = DEFAULT_CURRENCY) {
  const d = decimalsOf(code);
  return (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/** "UAE dirham (AED)" in the UI language — for a picker. */
export function currencyLabel(code, lang = 'en') {
  const c = CURRENCIES[normalizeCurrency(code)];
  return `${c.name[lang] || c.name.en} (${c.code})`;
}
